import { useEffect } from "react";
import withAuthentication from "../base/AuthComponent";
import { HttpRequest } from "../base/HttpModels";
import DrawerAppBarHOC from "../home/DrawerAppBar";
import { ORDER_LIST } from "../order_history/orderHistoryActions";
import { getFullOrderList } from "../order_history/ordersSlice";
import { useAppDispatch, useAppSelector } from "../redux/hooks";
import { IFullOrderListRequest, IOrderEntry } from "../order_history/ordersTypes";
import { isMobile } from "react-device-detect";
import { Box, Card, CardContent, Typography } from "@mui/material";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  BarElement,
  CategoryScale,
  LinearScale,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { IDishGroup } from "../menu/menuTypes";

// Register required components from Chart.js
ChartJS.register(
  BarElement,
  CategoryScale,
  LinearScale,
  Title,
  Tooltip,
  Legend
);

function calculateTotalGain(orders: IOrderEntry[], menu: IDishGroup[]) {
  var totalGain = 0;
  try {
    orders.forEach((order) => {
      order.order_lines.forEach((orderLine) => {
        // Find the price of the dish using dish_id
        const dish = menu
          .flatMap((category) => category.dishes)
          .find((dish) => dish.id === orderLine.dish_id);
        if (dish) {
          totalGain += orderLine.quantity * dish?.price;
        }
      });
    });
  } catch (e) {
    console.error(e);
  }
  return totalGain;
}

function calculateTotalQuantities(orders: IOrderEntry[]) {
  const quantitiesMap: { [dish_id: number]: number } = {};

  try {
    orders.forEach((order) => {
      order.order_lines.forEach((orderLine) => {
        if (quantitiesMap[orderLine.dish_id]) {
          quantitiesMap[orderLine.dish_id] += orderLine.quantity;
        } else {
          quantitiesMap[orderLine.dish_id] = orderLine.quantity;
        }
      });
    });
  } catch (e) {
    console.error(e);
  }
  return quantitiesMap;
}

function groupOrdersByDayAndMeal(orders: IOrderEntry[]) {
  const groups: { [key: string]: { lunch: IOrderEntry[], dinner: IOrderEntry[] } } = {};

  orders.forEach((order) => {
    const orderDate = new Date(order.creation_date);
    const dateStr = orderDate.toISOString().split('T')[0]; // YYYY-MM-DD format
    const hour = orderDate.getHours();
    
    // Initialize group if it doesn't exist
    if (!groups[dateStr]) {
      groups[dateStr] = { lunch: [], dinner: [] };
    }

    // Categorize as lunch or dinner based on time
    if (hour < 16) {
      groups[dateStr].lunch.push(order);
    } else {
      groups[dateStr].dinner.push(order);
    }
  });

  return { groups };
}

// Group orders by year for yearly dish-quantity charts
function groupOrdersByYear(orders: IOrderEntry[]) {
  const byYear: { [year: number]: IOrderEntry[] } = {};
  orders.forEach((order) => {
    const year = new Date(order.creation_date).getFullYear();
    if (!byYear[year]) {
      byYear[year] = [];
    }
    byYear[year].push(order);
  });
  return byYear;
}

function StatsComponentBody() {
  const orders = useAppSelector((state) => state.ordersState.fullOrders);
  const menu = useAppSelector((state) => state.menuState.menu);
  const token = useAppSelector((state) => state.loginState.token);
  const dispatch = useAppDispatch();

  useEffect(() => {
    if (token !== undefined && token !== null && token !== "") {
      const orderListRequest: HttpRequest<IFullOrderListRequest> = {
        action: ORDER_LIST,
        parameters: {
          user_token: token,
          full_list: true,
        },
      };
      dispatch(getFullOrderList(orderListRequest));
    }
  }, [dispatch, token]);

  // Group orders by day and meal type
  const { groups } = groupOrdersByDayAndMeal(orders);
  const ordersByYear = groupOrdersByYear(orders);

  // Generate chart data for each subgroup
  const generateChartData = (orders: IOrderEntry[]) => {
    const quantitiesMap = calculateTotalQuantities(orders);
    const sortedEntries = Object.entries(quantitiesMap).sort(
      ([, quantityA], [, quantityB]) => quantityB - quantityA
    );

    const labels = sortedEntries.map(([dish_id, quantity]) => {
      const name = (
        menu
          .flatMap((category) => category.dishes)
          .find((dish) => (dish.id + "") === dish_id)?.name ?? "Unknown"
      );
      return `${name} (${quantity})`;
    });

    const quantities = sortedEntries.map(([, quantity]) => quantity);

    return {
      labels: labels,
      datasets: [
        {
          label: "Quantities Ordered",
          data: quantities,
          backgroundColor: "rgba(75, 192, 192, 0.6)",
          borderColor: "rgba(75, 192, 192, 1)",
          borderWidth: 1,
        },
      ],
    };
  };

  // Chart.js options
  const options = {
    responsive: true,
    maintainAspectRatio: false as const,
    indexAxis: "y" as const, // Horizontal bar chart
    plugins: {
      title: {
        display: true,
        text: "Dish Quantities",
      },
    },
    scales: {
      x: {
        beginAtZero: true,
      },
    },
  };

  // Compute a reasonable chart height based on number of bars
  const getChartHeight = (bars: number) => {
    const perBar = 26; // px per bar for readability
    const base = 160;  // minimum height
    const max = 1600;  // cap to avoid excessive canvas height
    return Math.min(max, Math.max(base, bars * perBar));
  };

  // Helper function to format date for display
  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('it-IT', { 
      day: 'numeric', 
      month: 'numeric',
      year: 'numeric'
    });
  };

  return (
    <Box
      key={"menu_root"}
      sx={{
        width: "100%",
        paddingLeft: isMobile ? "0%" : "25%",
        paddingRight: isMobile ? "0%" : "25%",
        display: "block",
        alignItems: "left",
      }}
    >
      {/* Render charts for each day and meal type */}
      {Object.entries(groups)
        .sort(([dateA], [dateB]) => dateA.localeCompare(dateB))
        .map(([date, { lunch, dinner }]) => {
          const formattedDate = formatDate(date);
          const subGroups = [
            { label: `${formattedDate} Pranzo`, orders: lunch },
            { label: `${formattedDate} Cena`, orders: dinner },
          ];

          return subGroups.map(({ label, orders: subGroupOrders }) => {
            // Only render if there are orders in this subgroup
            if (subGroupOrders.length === 0) return null;

            const data = generateChartData(subGroupOrders);
            const chartHeight = getChartHeight(data.labels.length);
            const earnings = calculateTotalGain(subGroupOrders, menu);

            return (
              <Card
                variant="elevation"
                sx={{ width: "100%", marginBottom: "20px" }}
                key={label}
              >
                <CardContent>
                  <Bar
                    data={data}
                    height={chartHeight}
                    options={{
                      ...options,
                      plugins: { title: { display: true, text: label } },
                    }}
                  />
                </CardContent>
                <Typography variant="h6" align="center">
                  {earnings.toFixed(2)} €
                </Typography>
              </Card>
            );
          }).filter(Boolean); // Remove null entries
        })}

      {/* Yearly dish quantities (replace simple yearly total) */}
      {Object.entries(ordersByYear)
        .sort(([a], [b]) => Number(a) - Number(b))
        .map(([year, yearOrders]) => {
          const data = generateChartData(yearOrders);
          const chartHeight = getChartHeight(data.labels.length);
          const earnings = calculateTotalGain(yearOrders, menu);

          return (
            <Card
              variant="elevation"
              sx={{ width: "100%", marginBottom: "20px" }}
              key={`year-${year}`}
            >
              <CardContent>
                <Bar
                  data={data}
                  height={chartHeight}
                  options={{
                    ...options,
                    plugins: { title: { display: true, text: `Totale ${year}` } },
                  }}
                />
              </CardContent>
              <Typography variant="h6" align="center">
                {earnings.toFixed(2)} €
              </Typography>
            </Card>
          );
        })}
    </Box>
  );
}

function StatsComponent() {
  return (
    <DrawerAppBarHOC title="Statistiche" content={<StatsComponentBody />} />
  );
}

export default withAuthentication(StatsComponent);
